import { useMemo } from "react";
import { GitBranch } from "lucide-react";
import { useNetWorth, shortINR, formatINR } from "@/context/NetWorthContext";
import { branchTotal, findRoots } from "@/lib/tree";
import { Avatar } from "@/components/PersonCard";
import { t } from "@/lib/i18n";

export const BranchTotal = ({ p, tree }) => {
  const { map, visible } = useNetWorth();
  if (!visible || !tree.childrenOf(p.person_id).length) return null;
  const b = branchTotal(p.person_id, tree, map);
  return (
    <p className="text-sm text-[#52635B] py-3 border-t border-[#E5DEC9]" data-testid="branch-total">
      <GitBranch className="inline w-4 h-4 mr-1.5 text-[#C59B27]" />{t("Branch net worth")}: <b className="text-[#1E3A2B]">{formatINR(b.total)}</b> · {b.counted}/{b.members} {t("with net worth added")}
    </p>
  );
};

export const BranchWealth = ({ persons, tree, onSelect }) => {
  const { map, visible } = useNetWorth();
  const branches = useMemo(() => {
    if (!visible || !persons?.length) return [];
    const heads = [];
    findRoots(persons, tree).forEach((r) => {
      const kids = [r, ...tree.spousesOf(r.person_id)].flatMap((x) => tree.childrenOf(x.person_id));
      [...new Map(kids.map((k) => [k.person_id, k])).values()].forEach((k) => heads.push(k));
    });
    return heads.map((h) => ({ head: h, ...branchTotal(h.person_id, tree, map) })).sort((a, b) => b.total - a.total);
  }, [persons, tree, map, visible]);
  if (!branches.length) return null;
  const max = Math.max(1, ...branches.map((b) => b.total));
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-10" data-testid="branch-wealth">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-2 flex items-center gap-1.5"><GitBranch className="w-3.5 h-3.5" />{t("Branch wealth")}</p>
      <h2 className="font-serif text-2xl sm:text-3xl font-semibold mb-6">{t("Combined net worth of each family line")}</h2>
      <div className="grid md:grid-cols-2 gap-3">
        {branches.map((b) => (
          <button key={b.head.person_id} onClick={() => onSelect(b.head)} data-testid={`branch-${b.head.person_id}`}
            className="text-left rounded-2xl border border-[#E5DEC9] bg-white p-4 hover:border-[#1E3A2B] transition-colors">
            <div className="flex items-center gap-3">
              <Avatar p={b.head} size="w-10 h-10 text-sm" />
              <div className="flex-1 min-w-0">
                <p className="font-serif font-semibold truncate">{b.head.name} {t("line")}</p>
                <p className="text-xs text-[#7E8E85]">{b.counted}/{b.members} {t("with net worth added")}</p>
              </div>
              <p className="font-serif text-lg font-bold text-[#1E3A2B]" data-testid={`branch-total-${b.head.person_id}`}>{shortINR(b.total) || "₹0"}</p>
            </div>
            <div className="mt-3 h-2 rounded-full bg-[#F4EDE2] overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-[#C59B27] to-[#D96B43] transition-[width] duration-700" style={{ width: `${(b.total / max) * 100}%` }} />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};
